// Phase 6c — billing read hooks ({data, loading, error} with an alive guard),
// mirroring useAssets/useEdits. The Billing screen composes these; the WRITE flows
// go through components/BillingForm.tsx (top-level POST → PSP 303), not a hook.

import { useCallback, useEffect, useState } from 'react'
import { fetchBillingState, fetchCheckoutReturn, fetchInvoices, fetchPlans, fetchTopups } from '../lib/billing'
import { ApiError } from '../lib/api'
import type { BillingState, CheckoutReturn, InvoicesPage, PlansResponse, TopupsResponse } from '../lib/types'

export interface AsyncState<T> {
  data: T | null
  loading: boolean
  error: Error | null
}

/** AsyncState + an in-place `refetch()` (re-runs the load; GenericError onRetry). */
export type Async<T> = AsyncState<T> & { refetch: () => void }

function useAsync<T>(load: () => Promise<T>, deps: unknown[]): Async<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [nonce, setNonce] = useState(0)
  useEffect(() => {
    let alive = true
    setLoading(true)
    setError(null)
    load()
      .then((d) => { if (alive) setData(d) })
      .catch((e) => { if (alive) setError(e instanceof Error ? e : new Error(String(e))) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])
  const refetch = useCallback(() => setNonce((n) => n + 1), [])
  return { data, loading, error, refetch }
}

export function useBillingState(): Async<BillingState> {
  return useAsync<BillingState>(fetchBillingState, [])
}

export function usePlans(): Async<PlansResponse> {
  return useAsync<PlansResponse>(fetchPlans, [])
}

export function useTopups(): Async<TopupsResponse> {
  return useAsync<TopupsResponse>(fetchTopups, [])
}

export function useInvoices(opts: { limit?: number; offset?: number } = {}): Async<InvoicesPage> {
  const { limit, offset } = opts
  return useAsync<InvoicesPage>(() => fetchInvoices({ limit, offset }), [limit, offset])
}

/**
 * Verify a Stripe return, with a short bounded poll for webhook lag.
 *
 * Reloading starts the same owner-scoped read again.  Five background checks are
 * enough to bridge the common redirect-before-webhook race; after that the visible
 * pending state keeps a manual retry instead of polling the provider forever.
 */
export function useCheckoutReturn(sessionId: string | null, enabled: boolean): Async<CheckoutReturn> {
  const [result, setResult] = useState<{ sessionId: string; data: CheckoutReturn } | null>(null)
  const [loading, setLoading] = useState(enabled && !!sessionId)
  const [failure, setFailure] = useState<{ sessionId: string; error: Error } | null>(null)
  const [nonce, setNonce] = useState(0)
  const refetch = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    if (!enabled || !sessionId) {
      setResult(null)
      setLoading(false)
      setFailure(null)
      return
    }
    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined
    let attempt = 0

    const load = () => {
      attempt += 1
      if (attempt === 1) setLoading(true)
      fetchCheckoutReturn(sessionId)
        .then((result) => {
          if (!alive) return
          setResult({ sessionId, data: result })
          setFailure(null)
          setLoading(false)
          if (result.status === 'pending' && attempt < 5) {
            timer = setTimeout(load, 1600)
          }
        })
        .catch((cause) => {
          if (!alive) return
          setFailure({
            sessionId,
            error: cause instanceof Error ? cause : new Error(String(cause)),
          })
          setLoading(false)
          if (!(cause instanceof ApiError && cause.status === 404) && attempt < 5) {
            timer = setTimeout(load, 1600)
          }
        })
    }
    load()
    return () => {
      alive = false
      if (timer) clearTimeout(timer)
    }
  }, [enabled, sessionId, nonce])

  // Session changes are reflected during render, before the effect above runs.
  // An old success can therefore never flash under a new Session URL.
  const currentData = result?.sessionId === sessionId ? result.data : null
  const currentError = failure?.sessionId === sessionId ? failure.error : null
  const currentLoading = enabled && !!sessionId && (loading || (!currentData && !currentError))
  return { data: currentData, loading: currentLoading, error: currentError, refetch }
}
